<template>
  <div class="plans-page">

    <!-- Page Header -->
    <div class="page-header">
      <h5 class="page-title">Subscription</h5>
      <p class="page-sub">Manage your plan and view billing history</p>
    </div>

    <!-- Billing cycle toggle -->
    <div class="cycle-wrap">
      <button
        v-for="cycle in billingCycles"
        :key="cycle.key"
        class="cycle-btn"
        :class="{ 'cycle-btn--active': activeCycle === cycle.key }"
        @click="activeCycle = cycle.key"
      >
        {{ cycle.label }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="state-block">
      <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
      <p class="mt-3 text-muted-cell">Loading plans...</p>
    </div>

    <!-- Empty -->
    <div v-else-if="!activePlans.length" class="state-block">
      <div class="state-icon"><font-awesome-icon icon="tags" /></div>
      <p class="state-title">No plans available</p>
      <p class="state-sub">Please check back later or contact support.</p>
    </div>

    <!-- Plan cards -->
    <div v-else class="plans-grid">
      <div
        v-for="plan in activePlans"
        :key="plan.id"
        class="plan-card"
        :class="{ 'plan-card--popular': isPopular(plan) }"
      >
        <div v-if="isPopular(plan)" class="popular-badge">
          <font-awesome-icon icon="star" class="me-1" /> Most Popular
        </div>

        <div class="plan-name">{{ plan.name }}</div>

        <div class="plan-pricing">
          <template v-if="plan.price !== 'Custom'">
            <span class="plan-currency">{{ currentUserCurrency?.symbol ?? currencySymbol }}</span>
            <span class="plan-amount">{{ formatPrice(displayAmount(plan.price, currentUserCurrency)) }}</span>
          </template>
          <span v-else class="plan-custom">Custom</span>
        </div>
        <div class="plan-cycle">per {{ billingCycleLabel(plan.type) }}</div>

        <ul class="plan-features">
          <li v-for="(desc, idx) in planFeatures(plan)" :key="idx" class="plan-feature">
            <span class="feature-check"><font-awesome-icon icon="check" /></span>
            {{ desc }}
          </li>
        </ul>

        <div class="plan-cta">
          <button v-if="permission?.manage_subscription" class="btn-buy" :class="{ 'btn-buy--popular': isPopular(plan) }" @click="openPlanPayment(plan)">
            Get Started
          </button>
        </div>

        <div class="plan-link">
          <a href="https://mapiibs.co.uk/home" target="_blank" rel="noopener">mapiibs.co.uk</a>
        </div>
      </div>
    </div>

    <!-- ── Contact Us Modal ── -->
    <div class="modal-overlay" :class="{ open: contactModalOpen }" @click.self="contactModalOpen = false">
      <div class="modal-box">
        <div class="modal-hd">
          <h5 class="modal-ttl">Contact Us</h5>
          <button class="modal-close-btn" @click="contactModalOpen = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <div class="modal-bd">
          <div class="contact-icon-wrap"><font-awesome-icon icon="headset" /></div>
          <p class="contact-title">Talk to our team</p>
          <p class="contact-sub">Get in touch for a custom plan tailored to your business needs.</p>
          <div v-if="details?.tel || details?.tel2 || details?.email" class="contact-details">
            <div v-if="details?.tel || details?.tel2" class="contact-row">
              <font-awesome-icon icon="phone" class="contact-row-icon" />
              <span>{{ [details.tel, details.tel2].filter(Boolean).join(' · ') }}</span>
            </div>
            <div v-if="details?.email" class="contact-row">
              <font-awesome-icon icon="envelope" class="contact-row-icon" />
              <a :href="'mailto:' + details.email" class="contact-email-link">{{ details.email }}</a>
            </div>
          </div>
        </div>
        <div class="modal-ft">
          <button class="btn-ghost" @click="contactModalOpen = false">Close</button>
        </div>
      </div>
    </div>
    <div v-if="contactModalOpen" class="modal-backdrop" @click="contactModalOpen = false"></div>

    <!-- ── Payment + Login Modals (from pricingMixin) ── -->
    <PricingModals
      :loginModalOpen="loginModalOpen"
      :paymentModalOpen="paymentModalOpen"
      :currentData="currentData"
      :login="login"
      :validationErrors="validationErrors"
      :loginLoading="loginLoading"
      :countryCode="countryCode"
      :currencySymbol="currencySymbol"
      :localPaymentChannels="localPaymentChannels"
      :internationalPaymentChannels="internationalPaymentChannels"
      :backendUrl="backend_url.hostName"
      @close-login="loginModalOpen = false"
      @close-payment="paymentModalOpen = false"
      @update:login="login = $event"
      @login="loginUser"
      @flutterwave="makeFlutterwavePayment($event.data, $event.channel)"
      @paystack="makePaystackPayment($event.data, $event.channel)"
      @stripe="payWithStripe($event.data, $event.channel)"
    />

  </div>
</template>

<script>
import axios         from 'axios';
import PricingModals from '@/components/PricingModals.vue';
import { pricingMixin }  from '@/lib/PricingMixin';
import { reusableMixin } from '../../../lib/reusableMixin';

export default {
  name: 'SubscriptionPlans',
  components: { PricingModals },
  mixins: [pricingMixin, reusableMixin],

  data() {
    return {
      details:          {},
      contactModalOpen: false,
    };
  },

  methods: {
    isPopular(plan) { return plan.name?.toLowerCase() === 'business'; },

    billingCycleLabel(type) {
      const map = { Monthly: 'month', Quarterly: '3 months', 'Half Year': '6 months', Yearly: 'year' };
      return map[type] ?? type ?? '';
    },

    openPlanPayment(plan) {
      const normalised = {
        ...plan,
        amount: this.displayAmount(plan.price, this.currentUserCurrency) ?? plan.amount,
        descriptions: plan.descriptions ?? '[]',
      };
      this.paymentModal(normalised);
    },

    planFeatures(plan) {
      if (!plan) return [];
      const raw = plan.descriptions ?? plan.plan_detail?.descriptions ?? null;
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length) return parsed;
        } catch (_) {/* */}
      }
      const p = { ...plan, ...(plan.plan_detail ?? {}) };
      const feats = [];
      if (p.max_employee)  feats.push(p.max_employee === 0 ? 'Unlimited employees' : `Up to ${p.max_employee} employee${p.max_employee > 1 ? 's' : ''}`);
      if (p.max_product)   feats.push(p.max_product  === 0 ? 'Unlimited products'  : `Up to ${Number(p.max_product).toLocaleString()} products`);
      if (p.max_location)  feats.push(p.max_location === 0 ? 'Unlimited locations' : `Up to ${p.max_location} location${p.max_location > 1 ? 's' : ''}`);
      if (p.max_business)  feats.push(p.max_business === 0 ? 'Unlimited businesses': `Up to ${p.max_business} business${p.max_business > 1 ? 'es' : ''}`);
      if (p.max_sale)      feats.push(p.max_sale     === 0 ? 'Unlimited sales'     : `Up to ${Number(p.max_sale).toLocaleString()} sales`);
      if (p.max_purchase)  feats.push(p.max_purchase === 0 ? 'Unlimited purchases' : `Up to ${Number(p.max_purchase).toLocaleString()} purchases`);
      if (p.max_invoice)   feats.push(p.max_invoice  === 0 ? 'Unlimited invoices'  : `Up to ${Number(p.max_invoice).toLocaleString()} invoices`);
      if (p.max_billing)   feats.push(p.max_billing  === 0 ? 'Unlimited billings'  : `Up to ${Number(p.max_billing).toLocaleString()} billings`);
      if (p.max_expense)   feats.push(p.max_expense  === 0 ? 'Unlimited expenses'  : `Up to ${Number(p.max_expense).toLocaleString()} expenses`);
      if (p.sales_access)         feats.push('Sales management');
      if (p.purchase_access)      feats.push('Purchase management');
      if (p.invoice_access)       feats.push('Invoice management');
      if (p.billing_access)       feats.push('Billing management');
      if (p.expense_access)       feats.push('Expense tracking');
      if (p.hr_access)            feats.push('HR module');
      if (p.payroll_access)       feats.push('Payroll management');
      if (p.creditor_access)      feats.push('Creditors tracking');
      if (p.debitor_access)       feats.push('Debtors tracking');
      if (p.administrator_access) feats.push('Administration & roles');
      if (p.business_access)      feats.push('Multi-business management');
      if (p.settings_access)      feats.push('Settings');
      if (p.accounting_access)    feats.push('Accounting module');
      if (p.asset_access)         feats.push('Asset management');
      if (p.inventory_access)     feats.push('Inventory management');
      if (p.procurement_access)   feats.push('Procurement module');
      if (p.crm_access)           feats.push('CRM module');
      if (p.project_access)       feats.push('Project module');
      if (p.visitors_access)      feats.push('Visitor management');
      if (p.developer_access)     feats.push('Developer API');
      return feats;
    },

    async getActiveCategories() {
      try {
        const { data } = await axios.get('/subscription/category/active');
        this.categories        = data.data;
        this.planData.category = data.data[0]?.name ?? 'IMS';
      } catch (err) { console.log(err?.response?.data?.message); }
    },

    async getPlanTypes() {
      try {
        const { data } = await axios.get('/subscription/types');
        this.types           = data.data;
        const first          = data.data[Object.keys(data.data)[0]] ?? 'Monthly';
        this.activeCycleType = first;
        this.planData.type   = first;
      } catch (err) { console.log(err?.response?.data?.message); }
    },

    async pageData() {
      try {
        const { data } = await axios.get('/admin/fetchProjectSetup');
        this.details = data.data;
      } catch (err) { console.log(err?.response?.data?.message); }
    },

    fetchPlans(url = '/subscription/change') {
      this.loading = true;
      axios.get(url, { params: this.planData })
        .then(({ data }) => {
          const d = data.data;
          if      (Array.isArray(d))              this.plans = d;
          else if (Array.isArray(d?.plans))       this.plans = d.plans;
          else if (Array.isArray(d?.data?.plans)) this.plans = d.data.plans;
          else if (Array.isArray(d?.data))        this.plans = d.data;
          else                                    this.plans = [];
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
  },

  computed: {
    isSuperAdmin() { return this.store?.state?.getLoggedInUser?.is_super === 1; },
  },

  watch: {
    permission(p) {
      if (p !== null && !this.isSuperAdmin && !p?.manage_subscription) {
        this.$router.replace(this.$url('/hr/my-dashboard'));
      }
    },
  },

  async mounted() {
    const p = this.permission;
    if (p !== null && !this.isSuperAdmin && !p?.manage_subscription) {
      this.$router.replace(this.$url('/hr/my-dashboard'));
      return;
    }
    await this.initPricing();
    this.pageData();
  },
};
</script>

<style scoped>
/* ── Page ─────────────────────────────────────────────────────────── */
.plans-page { display:flex; flex-direction:column; gap:24px; }

/* ── Header ───────────────────────────────────────────────────────── */
.page-header { text-align:center; }
.page-title  { font-size:28px; font-weight:800; color:#1a2b45; margin:0 0 6px; }
.page-sub    { font-size:15px; color:#7a90b0; margin:0; }

/* ── Cycle toggle ─────────────────────────────────────────────────── */
.cycle-wrap { display:flex; justify-content:center; gap:6px; flex-wrap:wrap; }
.cycle-btn  { padding:8px 20px; border-radius:8px; border:1px solid #dde5f0; background:#fff; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; transition:all .15s; font-family:inherit; }
.cycle-btn:hover { background:#f4f7fb; color:#1a2b45; }
.cycle-btn--active { background:#004AAD; color:#fff; border-color:#004AAD; }

/* ── States ───────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

/* ── Plans grid ───────────────────────────────────────────────────── */
.plans-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(280px, 1fr)); gap:20px; max-width:1100px; margin:0 auto; width:100%; }

/* ── Plan card ────────────────────────────────────────────────────── */
.plan-card { position:relative; background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:32px 28px; display:flex; flex-direction:column; transition:box-shadow .2s, border-color .2s; }
.plan-card:hover { box-shadow:0 8px 32px rgba(0,0,0,.1); border-color:#c5d6f5; }
.plan-card--popular { border-color:#004AAD; border-width:2px; box-shadow:0 8px 32px rgba(0,74,173,.12); }
.popular-badge { position:absolute; top:-13px; left:50%; transform:translateX(-50%); background:#004AAD; color:#fff; border-radius:20px; padding:4px 14px; font-size:12px; font-weight:700; white-space:nowrap; display:flex; align-items:center; gap:5px; }
.plan-name    { font-size:15px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.06em; margin-bottom:20px; margin-top:8px; }
.plan-pricing { display:flex; align-items:flex-start; gap:3px; margin-bottom:4px; }
.plan-currency { font-size:18px; font-weight:700; color:#1a2b45; margin-top:8px; }
.plan-amount   { font-size:42px; font-weight:800; color:#1a2b45; line-height:1; }
.plan-custom   { font-size:36px; font-weight:800; color:#1a2b45; }
.plan-cycle    { font-size:12px; color:#7a90b0; font-weight:600; text-transform:uppercase; letter-spacing:.05em; margin-bottom:24px; }
.plan-features { list-style:none; padding:0; margin:0 0 28px; display:flex; flex-direction:column; gap:10px; flex:1; }
.plan-feature  { display:flex; align-items:flex-start; gap:10px; font-size:13px; color:#3d5068; line-height:1.4; }
.feature-check { width:20px; height:20px; border-radius:50%; background:#f0fdf4; color:#16a34a; display:flex; align-items:center; justify-content:center; font-size:10px; flex-shrink:0; margin-top:1px; }
.plan-cta  { margin-bottom:16px; }
.btn-buy   { width:100%; padding:11px 0; border-radius:9px; border:2px solid #004AAD; background:#fff; color:#004AAD; font-size:14px; font-weight:700; cursor:pointer; transition:all .15s; font-family:inherit; }
.btn-buy:hover { background:#004AAD; color:#fff; }
.btn-buy--popular { background:#004AAD; color:#fff; }
.btn-buy--popular:hover { background:#003b8e; border-color:#003b8e; }
.plan-link   { text-align:center; font-size:12px; }
.plan-link a { color:#004AAD; text-decoration:none; font-weight:600; }
.plan-link a:hover { text-decoration:underline; }

/* ── Contact modal ────────────────────────────────────────────────── */
.modal-overlay  { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
.modal-overlay.open { opacity:1; pointer-events:all; }
.modal-backdrop { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(3px); }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:420px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.16); overflow:hidden; transform:translateY(18px); transition:transform .25s ease; z-index:1051; position:relative; }
.modal-overlay.open .modal-box { transform:translateY(0); }
.modal-hd { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
.modal-ttl { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close-btn { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
.modal-close-btn:hover { background:#f4f7fb; }
.modal-bd { padding:28px 24px 20px; text-align:center; }
.modal-ft { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:center; }
.contact-icon-wrap { width:64px; height:64px; border-radius:50%; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:26px; margin:0 auto 16px; }
.contact-title     { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.contact-sub       { font-size:13px; color:#7a90b0; margin:0 0 20px; line-height:1.5; }
.contact-details   { display:flex; flex-direction:column; gap:10px; text-align:left; background:#f8fafc; border-radius:9px; padding:14px 16px; }
.contact-row       { display:flex; align-items:center; gap:10px; font-size:13px; color:#1a2b45; }
.contact-row-icon  { color:#004AAD; font-size:14px; width:16px; flex-shrink:0; }
.contact-email-link { color:#004AAD; text-decoration:none; font-weight:500; }
.contact-email-link:hover { text-decoration:underline; }
.btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 24px; border-radius:8px; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-ghost:hover { background:#e4eaf2; }
.me-1 { margin-right:4px; }

/* ── Responsive ───────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .plans-grid { grid-template-columns: 1fr; }
}
</style>
